/**
 * Subdomain Redirect Handler
 * Handles client-side redirects for multilanguage subdomains based on theme settings
 */

window.theme = window.theme || {};

theme.subdomainRedirects = {
  /**
   * Initialize the redirect handler
   */
  init: function() {
    // Check if redirects are enabled
    if (!window.themeSettings || !window.themeSettings.enable_subdomain_redirects) {
      console.log('Subdomain Redirects: Disabled or not configured');
      return;
    }

    // Get redirect rules from theme settings
    const redirectRules = this.getRedirectRules();
    if (!redirectRules || redirectRules.length === 0) {
      console.log('Subdomain Redirects: No redirect rules found');
      return;
    }

    // Check if current page matches any redirect rule
    const redirectUrl = this.checkRedirectRules(redirectRules);
    if (redirectUrl) {
      this.performRedirect(redirectUrl);
    } else {
      console.log('Subdomain Redirects: No matching redirect rule found for current page');
    }
  },

  /**
   * Parse redirect rules from theme settings
   * @returns {Array} Array of redirect rule objects
   */
  getRedirectRules: function() {
    try {
      if (window.themeSettings && window.themeSettings.redirect_rules) {
        const rules = JSON.parse(window.themeSettings.redirect_rules);
        console.log('Subdomain Redirects: Loaded', rules.length, 'redirect rules');
        return rules;
      }
    } catch (error) {
      console.error('Subdomain Redirects: Invalid redirect rules JSON format:', error);
    }
    return [];
  },

  /**
   * Check if current page matches any redirect rules
   * @param {Array} redirectRules - Array of redirect rule objects
   * @returns {string|null} Redirect URL if match found, null otherwise
   */
  checkRedirectRules: function(redirectRules) {
    const currentDomain = window.location.hostname;
    const currentPath = window.location.pathname;

    console.log('Subdomain Redirects: Checking rules for domain:', currentDomain, 'path:', currentPath);

    for (const rule of redirectRules) {
      console.log('Subdomain Redirects: Checking rule:', rule);
      
      // Check if domain matches exactly
      if (rule.domain === currentDomain) {
        console.log('Subdomain Redirects: Domain matches!');
        
        // Check if path matches exactly
        if (rule.path === currentPath) {
          console.log('Subdomain Redirects: Path matches! Redirect will happen.');
          return rule.redirect_to;
        } else {
          console.log('Subdomain Redirects: Path does not match. Expected:', rule.path, 'Got:', currentPath);
        }
      } else {
        console.log('Subdomain Redirects: Domain does not match. Expected:', rule.domain, 'Got:', currentDomain);
      }
    }

    console.log('Subdomain Redirects: No matching rule found');
    return null;
  },

  /**
   * Perform the redirect
   * @param {string} redirectUrl - URL to redirect to
   */
  performRedirect: function(redirectUrl) {
    // Add a small delay to ensure page is loaded and for better UX
    setTimeout(() => {
      console.log('Subdomain Redirects: Redirecting from', window.location.pathname, 'to', redirectUrl);
      
      // Check if we're in design mode (Shopify admin)
      if (window.Shopify && window.Shopify.designMode) {
        console.log('Subdomain Redirects: In design mode, showing alert instead of redirecting');
        alert(`Redirect would go to: ${redirectUrl}\n\nThis is a preview - redirects are disabled in design mode.`);
        return;
      }
      
      window.location.href = redirectUrl;
    }, 200);
  },

  /**
   * Add a new redirect rule (for future use)
   * @param {string} domain - Domain to match
   * @param {string} path - Path to match
   * @param {string} redirectTo - URL to redirect to
   */
  addRedirectRule: function(domain, path, redirectTo) {
    const redirectRules = this.getRedirectRules();
    redirectRules.push({
      domain: domain,
      path: path,
      redirect_to: redirectTo
    });
    
    // This would need to be saved back to theme settings
    // For now, just log the new rule
    console.log('Subdomain Redirects: New rule added:', { domain, path, redirect_to: redirectTo });
  },

  /**
   * Remove a redirect rule (for future use)
   * @param {string} domain - Domain to match
   * @param {string} path - Path to match
   */
  removeRedirectRule: function(domain, path) {
    const redirectRules = this.getRedirectRules();
    const filteredRules = redirectRules.filter(rule => 
      !(rule.domain === domain && rule.path === path)
    );
    
    // This would need to be saved back to theme settings
    console.log('Subdomain Redirects: Rule removed:', { domain, path });
  },

  /**
   * Get current redirect status for debugging
   * @returns {Object} Current status information
   */
  getStatus: function() {
    const rules = this.getRedirectRules();
    const matchingRule = this.checkRedirectRules(rules);
    
    return {
      enabled: window.themeSettings ? window.themeSettings.enable_subdomain_redirects : false,
      currentDomain: window.location.hostname,
      currentPath: window.location.pathname,
      fullUrl: window.location.href,
      rules: rules,
      rulesCount: rules.length,
      matchingRule: matchingRule,
      willRedirect: !!matchingRule
    };
  },

  /**
   * Test a specific redirect rule manually
   * @param {string} testDomain - Domain to test
   * @param {string} testPath - Path to test
   * @returns {Object} Test result
   */
  testRule: function(testDomain, testPath) {
    const rules = this.getRedirectRules();
    const currentDomain = window.location.hostname;
    const currentPath = window.location.pathname;
    
    console.log('Subdomain Redirects: Testing rule for domain:', testDomain, 'path:', testPath);
    console.log('Subdomain Redirects: Current domain:', currentDomain, 'path:', currentPath);
    
    for (const rule of rules) {
      if (rule.domain === testDomain && rule.path === testPath) {
        return {
          found: true,
          rule: rule,
          wouldRedirect: true,
          redirectTo: rule.redirect_to
        };
      }
    }
    
    return {
      found: false,
      wouldRedirect: false,
      redirectTo: null
    };
  }
};

// Initialize redirects when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  theme.subdomainRedirects.init();
});

// Also initialize on page load for SPA-like behavior
document.addEventListener('page:loaded', function() {
  theme.subdomainRedirects.init();
});

// Make it available globally for debugging
window.subdomainRedirects = theme.subdomainRedirects;
